import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import RedeemAccessCode from "@/components/RedeemAccessCode";
import { isNative } from "@/lib/native";
import { Badge } from "@/components/ui/badge";
import ManageBilling from "@/components/ManageBilling";
import AccountSettings from "@/components/AccountSettings";
import InvoiceRequestsTable from "@/components/InvoiceRequestsTable";
import { CreditCard, Receipt, Download, ExternalLink, Loader2, Package, Gift, Users, ArrowRight } from "lucide-react";
import { logError } from "@/lib/log";

const TIER_LABEL = { support: "Ongoing Support", starter: "Starter Support", professional: "Professional Manager", enterprise: "Enterprise Multi-site" };
const fmtDate = (v) => (v ? new Date(typeof v === "number" ? v * 1000 : v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtMoney = (n, cur = "gbp") => new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "gbp").toUpperCase() }).format(n || 0);
const STATUS_STYLE = { paid: "bg-accent/10 text-accent", open: "bg-amber-100 text-amber-800", void: "bg-muted text-muted-foreground", uncollectible: "bg-destructive/10 text-destructive", draft: "bg-muted text-muted-foreground" };

const planStatus = (active, sub) => {
  if (!active) return { cls: "", label: "Inactive" };
  if (sub.cancel_at_period_end) return { cls: "bg-amber-100 text-amber-800 border-0", label: "Ending" };
  return { cls: "bg-accent/10 text-accent border-0", label: "Active" };
};

function PlanDetail({ data, sub, yearly }) {
  const { user } = useAuth();
  if (data.via_team) {
    return <p className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1" data-testid="billing-plan-detail"><Users className="w-3.5 h-3.5" /> Via {data.team_owner_name}'s team</p>;
  }
  if (sub.subscription_source === "admin_invite") {
    return <p className="text-sm text-muted-foreground mt-1" data-testid="billing-plan-detail">Complimentary access{sub.subscription_period_end ? ` · until ${fmtDate(sub.subscription_period_end)}` : " · no expiry"}</p>;
  }
  if (sub.subscription_source === "access_code") {
    return <p className="text-sm text-muted-foreground mt-1" data-testid="billing-plan-detail">Complimentary access · code {sub.access_code}{sub.subscription_period_end ? ` · until ${fmtDate(sub.subscription_period_end)}` : " · no expiry"}</p>;
  }
  const trialEnd = user?.subscription_trial_end;
  if (trialEnd && new Date(trialEnd) > new Date()) {
    return <p className="text-sm text-muted-foreground mt-1" data-testid="billing-plan-detail">Free trial · {sub.cancel_at_period_end ? "access until" : "first payment on"} {fmtDate(trialEnd)}{yearly ? " (yearly)" : " (monthly)"}</p>;
  }
  const renewal = sub.subscription_period_end ? ` · ${sub.cancel_at_period_end ? "access until" : "renews"} ${fmtDate(sub.subscription_period_end)}` : "";
  return <p className="text-sm text-muted-foreground mt-1" data-testid="billing-plan-detail">{yearly ? "Billed yearly" : "Billed monthly"}{renewal}</p>;
}

function PlanCard({ data, user, reload }) {
  const sub = data.subscription;
  const active = sub.subscription_status === "active";
  const yearly = (sub.subscription_lookup_key || "").endsWith("_yearly");
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="billing-plan-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-accent">Current plan</p>
          <h2 className="font-display text-xl font-bold mt-1" data-testid="billing-plan-name">
            {active ? TIER_LABEL[sub.subscription_tier] || sub.subscription_tier : "No active subscription"}
          </h2>
          {active && <PlanDetail data={data} sub={sub} yearly={yearly} />}
        </div>
        <Badge variant="secondary" className={planStatus(active, sub).cls} data-testid="billing-plan-status">{planStatus(active, sub).label}</Badge>
      </div>
      {data.credit_gbp > 0 && (
        <p className="mt-4 text-sm inline-flex items-center gap-2 rounded-xl bg-accent/10 text-accent px-3 py-2" data-testid="billing-credit">
          <Gift className="w-4 h-4" /> {fmtMoney(data.credit_gbp)} referral credit will be applied to your next invoice
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {data.has_billing && !data.via_team && <ManageBilling onChanged={reload} />}
        {!active && !isNative && <Button asChild data-testid="billing-choose-plan"><Link to="/pricing">Choose a plan <ArrowRight className="w-4 h-4 ml-1.5" /></Link></Button>}
      </div>
    </section>
  );
}

function InvoicesTable({ invoices }) {
  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid="billing-invoices">
      <div className="p-6 border-b border-border flex items-center gap-2">
        <Receipt className="w-5 h-5 text-primary" />
        <h2 className="font-display text-lg font-bold">Invoices</h2>
      </div>
      {invoices.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground" data-testid="billing-invoices-empty">No invoices yet. Subscription invoices appear here once your first payment is taken.</p>
      ) : (
        <div className="divide-y divide-border">
          {invoices.map((inv) => (
            <div key={inv.id} className="p-4 sm:px-6 flex items-center gap-4 flex-wrap" data-testid={`invoice-row-${inv.id}`}>
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-medium">{inv.number || inv.id}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(inv.date)}{inv.description ? ` · ${inv.description}` : ""}</p>
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-wider rounded px-2 py-0.5 ${STATUS_STYLE[inv.status] || "bg-muted"}`}>{inv.status}</span>
              <span className="font-display font-bold text-sm w-20 text-right">{fmtMoney(inv.amount, inv.currency)}</span>
              <div className="flex gap-1">
                {inv.hosted_url && <Button asChild variant="ghost" size="sm" data-testid={`invoice-view-${inv.id}`}><a href={inv.hosted_url} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>}
                {inv.pdf_url && <Button asChild variant="ghost" size="sm" data-testid={`invoice-pdf-${inv.id}`}><a href={inv.pdf_url} target="_blank" rel="noreferrer"><Download className="w-4 h-4" /></a></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PurchasesTable({ purchases }) {
  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden" data-testid="billing-purchases">
      <div className="p-6 border-b border-border flex items-center gap-2">
        <Package className="w-5 h-5 text-primary" />
        <h2 className="font-display text-lg font-bold">Payment history</h2>
      </div>
      {purchases.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground" data-testid="billing-purchases-empty">No payments yet.</p>
      ) : (
        <div className="divide-y divide-border">
          {purchases.map((p) => (
            <div key={p.session_id} className="p-4 sm:px-6 flex items-center gap-4" data-testid={`purchase-row-${p.session_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.title}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(p.updated_at || p.created_at)}{p.referral_discount ? " · referral discount applied" : ""}</p>
              </div>
              <span className="font-display font-bold text-sm">{fmtMoney(p.amount, p.currency)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const reload = useCallback(() => {
    api.get("/billing/overview").then((r) => setData(r.data)).catch((err) => { logError("Failed to load billing", err); setError(true); });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  if (error) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-sm text-muted-foreground" data-testid="billing-error">We couldn't load your billing details. Please try again shortly.</div>;
  if (!data) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="billing-page">
      <div className="mb-8">
        <span className="overline text-accent inline-flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Account</span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Billing & account</h1>
        <p className="text-muted-foreground mt-2 text-sm">Manage your subscription, update your card, and download invoices for {user?.org_name || "your service"}.</p>
      </div>
      <div className="space-y-6">
        <PlanCard data={data} user={user} reload={reload} />
        {!(data.subscription.subscription_status === "active" && data.has_billing) && <RedeemAccessCode onRedeemed={reload} />}
        <InvoiceRequestsTable />
        <InvoicesTable invoices={data.invoices} />
        <PurchasesTable purchases={data.purchases} />
        <AccountSettings />
      </div>
    </div>
  );
}
